/**
 * @ipc/validation · src/schemas/refunds.ts — zod-схемы (Zod v4) для границы
 * возвратов и фискализации (раздел 06, tech-spec/06-refunds-fiscal.md).
 * Валидирует тела запросов нашего API (частичный возврат, фискальная ссылка)
 * и оборачивает ответ Multicard `taxReceiptModel` (результат фискализации).
 */
import { z } from 'zod/v4';

/** Тийин — целая положительная сумма (1 сум = 100 тийин). */
const tiyinPositive = z.number().int().positive();

/** Сервисы-приложения, для которых при возврате обязателен полный номер карты (раздел 06). */
export const APP_REFUND_SERVICES = [
  'payme', 'click', 'uzum', 'anorbank', 'oson',
  'alif', 'xazna', 'beepul', 'trastpay', 'sbp',
] as const;

/** Позиция фискального чека (ofdRequest[] у Multicard). Обязательные поля — по доке. */
export const ofdItemSchema = z.object({
  qty: z.number().int().positive(), // количество единиц
  price: tiyinPositive, // цена за единицу, тийины
  mxik: z.string().min(1), // ИКПУ (tasnif.soliq.uz)
  total: tiyinPositive, // сумма позиции без скидок, тийины
  package_code: z.string().min(1), // код упаковки (tasnif.soliq.uz)
  name: z.string().min(1), // наименование товара/услуги
  vat: z.number().int().min(0).optional(), // НДС, %
  tin: z.string().optional(), // ИНН компании
  mark: z.array(z.string()).optional(), // коды маркировки (обяз. для маркир. товаров)
});
export type OfdItem = z.infer<typeof ofdItemSchema>;

/** Тело частичного возврата нашего API: POST /api/v1/payments/:id/refund/partial. */
export const partialRefundBodySchema = z
  .object({
    refundAmount: tiyinPositive,
    ofd: z.array(ofdItemSchema).min(1),
    cardPan: z.string().min(12).max(19).optional(),
  })
  .refine(
    (b) => b.ofd.reduce((s, i) => s + i.total, 0) === b.refundAmount,
    { message: 'sum(ofd[].total) must equal refundAmount', path: ['refundAmount'] },
  );
export type PartialRefundBody = z.infer<typeof partialRefundBodySchema>;

/**
 * cardPan обязателен, если оплата шла через платёжное приложение.
 * `ps` платежа известен на бэке (из Payment), поэтому проверку делаем
 * фабрикой схемы, а не статическим refine (ps не приходит в теле запроса).
 */
export function partialRefundBodyForService(ps: string | null | undefined) {
  const requiresCardPan = ps !== null && ps !== undefined && (APP_REFUND_SERVICES as readonly string[]).includes(ps);
  return partialRefundBodySchema.refine(
    (b) => (requiresCardPan ? typeof b.cardPan === 'string' && b.cardPan.length > 0 : true),
    { message: 'cardPan is required for payment-app services', path: ['cardPan'] },
  );
}

/** Тело отправки фискальной ссылки нашего API: POST /api/v1/payments/:id/fiscal-link. */
export const fiscalLinkBodySchema = z.object({
  url: z.url(),
  isRefund: z.boolean().optional(),
});
export type FiscalLinkBody = z.infer<typeof fiscalLinkBodySchema>;

/**
 * Форма частичного возврата в admin-ui (react-hook-form + zodResolver).
 * Оператор вводит сумму в СУМАХ (не тийинах) и ОДНУ фискальную позицию;
 * admin-ui конвертирует sumAmount×100 → refundAmount (тийины) и строит
 * ofd[] из этой единственной позиции с total==refundAmount при сборке тела
 * запроса (см. `partialRefundBodySchema`) — здесь только форма ввода.
 */
export const partialRefundFormSchema = z.object({
  refundAmountSum: z.coerce.number().positive({ message: 'Сумма возврата должна быть больше нуля' }),
  itemName: z.string().min(1, { message: 'Укажите наименование позиции' }).max(255),
  mxik: z.string().min(1, { message: 'Укажите ИКПУ (mxik)' }),
  packageCode: z.string().min(1, { message: 'Укажите код упаковки' }),
  qty: z.coerce.number().int().positive({ message: 'Количество должно быть целым положительным числом' }),
  cardPan: z.string().min(12).max(19).optional().or(z.literal('')),
});
export type PartialRefundFormDto = z.infer<typeof partialRefundFormSchema>;
/** Вход формы ДО coerce (refundAmountSum/qty приходят как string из <input>). */
export type PartialRefundFormInput = z.input<typeof partialRefundFormSchema>;

/** taxReceiptModel — результат фискализации из paymentModel.tax / .refund_tax (данность Multicard). */
export const taxReceiptSchema = z.object({
  receipt: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).nullable(), // объект чека, отправленный в ГНК
  f_num: z.string(), // Фискальный признак (не путать с fm_num)
  fm_num: z.string(), // Фискальный терминал
  qr_url: z.string().nullable(), // URL на фискальный чек ГНК
  is_refund: z.boolean(), // является ли чеком возврата
});
export type TaxReceipt = z.infer<typeof taxReceiptSchema>;
